import { useEffect, useMemo, useState } from "react";
import { Check, CheckCircle2, CircleDot, FileJson2, ShieldCheck } from "lucide-react";
import { procurementAuditWorkspace } from "@/lib/api";
import { goldenJourneyProgress, isDirectProcurementRoute } from "@/lib/golden-journey.mjs";
import { ProcurementDocumentActions } from "@/procurement-documents";
import { Badge } from "@/components/ui/badge";
import { displayDate, money, titleCase } from "@/lib/utils";

type Workspace = Awaited<ReturnType<typeof procurementAuditWorkspace>>;

type JourneyEvent = {
  id: string;
  occurredAt: string;
  stage: string;
  title: string;
  summary: string;
  actor: string;
  actorRole?: string | null;
  source: string;
};

type Journey = {
  id: string;
  caseId?: string;
  requestNo: string;
  title: string;
  status: string;
  estimatedValue: number;
  route?: string;
  events: JourneyEvent[];
};

const lifecycleLabels: Record<string, [string, string]> = {
  "purchase_order.released": ["Order", "Purchase order released"],
  "purchase_order.acknowledged": ["Supplier", "Supplier acknowledged order"],
  "invoice.matched": ["Match", "Invoice matched"],
  "invoice.exception": ["Match", "Invoice exception recorded"],
  "payment.due": ["Pay", "Payment became due"],
  "payment.approved": ["Pay", "Payment approved"],
  "payment.initiated": ["Pay", "Payment initiated"],
  "payment.completed": ["Pay", "Payment completion recorded"],
  "payment.reconciled": ["Close", "Payment reconciled"],
  "transaction.closed": ["Close", "Procurement case closed"],
  "procurement_document.sealed": ["Evidence", "Procurement document sealed"],
};

const fulfilmentLabels: Record<string, string> = {
  goods_dispatch: "Goods dispatched",
  inspection: "Inspection recorded",
  service_entry: "Service delivery recorded",
  milestone: "Milestone completed",
  installation: "Installation recorded",
  commissioning: "Commissioning recorded",
  acceptance: "Operational acceptance recorded",
  return: "Return recorded",
  replacement: "Replacement recorded",
};

function profileName(profile: unknown) {
  const value = Array.isArray(profile) ? profile[0] : profile;
  if (!value || typeof value !== "object") return "System";
  const row = value as {
    full_name?: string | null;
    name?: string | null;
    email?: string | null;
  };
  return row.full_name || row.name || row.email || "System";
}

function text(value: unknown) {
  return typeof value === "string" ? value : "";
}

function matches(query: string, ...values: unknown[]) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return values.some((value) =>
    String(value || "")
      .toLowerCase()
      .includes(needle),
  );
}

function buildJourneys(data: Workspace): Journey[] {
  const requisitions = new Map(data.requisitions.map((row: any) => [row.id, row]));
  const rfqs = new Map(data.rfqs.map((row: any) => [row.id, row]));
  const purchaseOrders = new Map(
    data.purchaseOrders.map((row: any) => [row.id, row]),
  );
  const casesById = new Map(data.cases.map((row: any) => [row.id, row]));
  const casesByRequisition = new Map(
    data.cases.map((row: any) => [row.requisition_id, row]),
  );
  const invoices = new Map(data.invoices.map((row: any) => [row.id, row]));
  const events = new Map<string, JourneyEvent[]>();

  function add(requisitionId: string | undefined, event: JourneyEvent) {
    if (!requisitionId || !requisitions.has(requisitionId)) return;
    events.set(requisitionId, [...(events.get(requisitionId) || []), event]);
  }

  function requisitionForOrder(orderId: string | undefined) {
    const order = orderId ? (purchaseOrders.get(orderId) as any) : undefined;
    const rfq = order ? (rfqs.get(order.rfq_id) as any) : undefined;
    return (order?.requisition_id || rfq?.requisition_id) as string | undefined;
  }

  for (const row of data.requisitions as any[]) {
    add(row.id, {
      id: `requisition-${row.id}`,
      occurredAt: row.created_at,
      stage: "Request",
      title: "Purchase requested",
      summary: `${row.request_no} · ${row.title}`,
      actor: profileName(row.actor),
      source: "requisition",
    });
  }

  for (const row of data.approvals as any[]) {
    add(row.requisition_id, {
      id: `approval-${row.id}`,
      occurredAt: row.created_at,
      stage: "Approve",
      title: row.decision === "approved" ? "Request approved" : "Request rejected",
      summary: row.comments || "Decision recorded without a comment",
      actor: profileName(row.actor),
      source: "requisition_approval",
    });
  }

  for (const row of data.rfqs as any[]) {
    add(row.requisition_id, {
      id: `rfq-${row.id}`,
      occurredAt: row.created_at,
      stage: "Source",
      title: "Sourcing event created",
      summary: `${row.rfq_no} · ${row.title}`,
      actor: profileName(row.actor),
      source: "rfq",
    });
  }

  for (const row of data.purchaseOrders as any[]) {
    const rfq = rfqs.get(row.rfq_id) as any;
    const direct = isDirectProcurementRoute(row.procurement_route);
    const routeTitle =
      row.procurement_route === "external_purchase_order"
        ? "External purchase order recorded"
        : row.procurement_route === "emergency_purchase"
          ? "Emergency purchase order created"
          : direct
            ? "Direct purchase order created"
            : "Supplier awarded and order created";
    add(row.requisition_id || rfq?.requisition_id, {
      id: `award-${row.id}`,
      occurredAt: row.created_at,
      stage: direct ? "Order" : "Award",
      title: routeTitle,
      summary: [
        row.po_no,
        profileName(row.vendors),
        money(row.total, row.currency),
        row.external_reference,
        row.route_reason,
      ].filter(Boolean).join(" · "),
      actor: profileName(row.actor),
      source: "purchase_order",
    });
  }

  for (const row of data.receipts as any[]) {
    add(requisitionForOrder(row.purchase_order_id), {
      id: `receipt-${row.id}`,
      occurredAt: row.received_at,
      stage: "Receive",
      title: "Goods received",
      summary: `${row.grn_no}${row.notes ? ` · ${row.notes}` : ""}`,
      actor: profileName(row.actor),
      source: "goods_receipt",
    });
  }

  for (const row of data.fulfilment as any[]) {
    if (row.kind === "goods_receipt") continue;
    const procurementCase = casesById.get(row.procurement_case_id) as any;
    const performedBy = text(row.details?.performed_by);
    const note = text(row.details?.note);
    add(procurementCase?.requisition_id, {
      id: `fulfilment-${row.id}`,
      occurredAt: row.occurred_at || row.created_at,
      stage: "Fulfil",
      title: fulfilmentLabels[row.kind] || `${titleCase(row.kind)} recorded`,
      summary: [performedBy && `Performed by ${performedBy}`, row.reference_no, note]
        .filter(Boolean)
        .join(" · "),
      actor: profileName(row.actor),
      source: "fulfilment_record",
    });
  }

  for (const row of data.invoices as any[]) {
    add(requisitionForOrder(row.purchase_order_id), {
      id: `invoice-${row.id}`,
      occurredAt: row.created_at,
      stage: "Invoice",
      title: "Invoice recorded",
      summary: `${row.invoice_no} · ${money(Number(row.amount) + Number(row.tax_amount || 0))}`,
      actor: profileName(row.actor),
      source: "invoice",
    });
  }

  for (const row of data.audit as any[]) {
    const label = lifecycleLabels[row.action];
    if (!label || !row.procurement_case_id) continue;
    const procurementCase = casesById.get(row.procurement_case_id) as any;
    let context = row.reason || "";
    const orderId = row.metadata?.purchase_order_id ||
      (row.entity_type === "purchase_order" ? row.entity_id : undefined);
    const order = orderId ? (purchaseOrders.get(orderId) as any) : undefined;
    const invoice = row.entity_type === "invoice" ? (invoices.get(row.entity_id) as any) : undefined;
    if (order)
      context = `${order.po_no} · ${profileName(order.vendors)} · ${money(order.total, order.currency)}`;
    else if (invoice) context = invoice.invoice_no;
    else if (!context && row.previous_state && row.new_state)
      context = `${titleCase(row.previous_state)} → ${titleCase(row.new_state)}`;
    add(procurementCase?.requisition_id, {
      id: `lifecycle-${row.id}`,
      occurredAt: row.created_at,
      stage: label[0],
      title:
        row.action === "procurement_document.sealed"
          ? row.metadata?.document_kind === "audit_pack"
            ? "Audit pack sealed"
            : row.metadata?.document_kind === "file_note"
              ? "File note sealed"
              : label[1]
          : label[1],
      summary: context || "Governed lifecycle event",
      actor: row.actor_name || row.metadata?.actor_snapshot?.name || profileName(row.profiles),
      actorRole: row.actor_role || row.metadata?.actor_snapshot?.role,
      source: "audit_event",
    });
  }

  return (data.requisitions as any[])
    .map((row) => ({
      id: row.id,
      caseId: (casesByRequisition.get(row.id) as any)?.id,
      requestNo: row.request_no,
      title: row.title,
      status: row.status,
      estimatedValue: Number(row.estimated_value || 0),
      route: (data.purchaseOrders as any[]).find(
        (order) => order.requisition_id === row.id,
      )?.procurement_route,
      events: (events.get(row.id) || []).sort(
        (a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt),
      ),
    }))
    .sort((a, b) => {
      const aTime = a.events.at(-1)?.occurredAt || "";
      const bTime = b.events.at(-1)?.occurredAt || "";
      return Date.parse(bTime) - Date.parse(aTime);
    });
}

export function ProcurementAudit({
  organisationId,
  refresh,
  query,
}: {
  organisationId: string;
  refresh: number;
  query: string;
}) {
  const [data, setData] = useState<Workspace>();
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setError("");
    procurementAuditWorkspace(organisationId)
      .then((value) => live && setData(value))
      .catch((reason: Error) => live && setError(reason.message));
    return () => {
      live = false;
    };
  }, [organisationId, refresh]);

  const journeys = useMemo(() => (data ? buildJourneys(data) : []), [data]);
  const visible = useMemo(
    () =>
      journeys.filter((journey) =>
        matches(
          query,
          journey.requestNo,
          journey.title,
          journey.status,
          ...journey.events.flatMap((event) => [
            event.stage,
            event.title,
            event.summary,
            event.actor,
          ]),
        ),
      ),
    [journeys, query],
  );
  const attributableEvents = journeys.reduce(
    (count, journey) => count + journey.events.filter((event) => event.actor !== "System").length,
    0,
  );
  const operationalEvidence = data?.fulfilment.filter(
    (row: any) => row.kind !== "goods_receipt",
  ).length || 0;
  const completedJourneys = journeys.filter(
    (journey) => goldenJourneyProgress(journey).complete,
  ).length;

  if (error)
    return (
      <div className="rounded-2xl border border-[var(--error-border)] bg-[var(--error-soft)] p-6">
        <h2 className="font-serif text-xl">Unable to load the audit journey</h2>
        <p className="mt-2 text-sm text-[var(--error)]">{error}</p>
      </div>
    );
  if (!data)
    return (
      <div className="surface p-8 text-center text-sm text-[var(--text-secondary)]">
        Building the accountable purchase journeys…
      </div>
    );

  return (
    <div className="grid gap-5">
      <header className="page-heading">
        <div>
          <p className="eyebrow">CONTROL EVIDENCE</p>
          <h1>Procurement audit trail</h1>
          <p>
            See who requested, approved, sourced, ordered, received, installed,
            accepted, invoiced, paid and closed every purchase.
          </p>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AuditMetric icon={ShieldCheck} label="Purchase journeys" value={journeys.length} />
        <AuditMetric icon={CheckCircle2} label="Complete journeys" value={completedJourneys} />
        <AuditMetric icon={CheckCircle2} label="Named-actor events" value={attributableEvents} />
        <AuditMetric icon={CircleDot} label="Operational evidence" value={operationalEvidence} />
      </section>

      <section className="grid gap-4" aria-label="Purchase journeys">
        {visible.map((journey) => {
          const progress = goldenJourneyProgress(journey);
          return (
          <article className="surface overflow-hidden" key={journey.id}>
            <header className="flex flex-col gap-3 border-b border-[var(--border)] p-5 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-bold tracking-[.12em] text-[var(--teal)]">
                  {journey.requestNo}
                </p>
                <h2 className="mt-1 font-serif text-2xl">{journey.title}</h2>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  Estimated {money(journey.estimatedValue)} · {journey.events.length} recorded events · {isDirectProcurementRoute(journey.route) ? titleCase(journey.route) : "Competitive sourcing"}
                </p>
              </div>
              <div className="flex flex-col items-start gap-3 sm:items-end">
                <Badge value={journey.status} />
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${progress.complete ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
                  {progress.completed}/{progress.total} journey gates
                </span>
                {journey.caseId && (
                  <ProcurementDocumentActions
                    organisationId={organisationId}
                    journey={{ ...journey, id: journey.caseId }}
                  />
                )}
              </div>
            </header>
            <div className="border-b border-[var(--border)] bg-[var(--surface-secondary)] p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                <strong>{progress.complete ? "Golden Journey complete" : `Next evidence: ${progress.next?.label}`}</strong>
                <span className="text-[var(--text-secondary)]">{progress.percent}% evidenced</span>
              </div>
              <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" aria-label={`${journey.requestNo} journey gates`}>
                {progress.milestones.map((milestone) => (
                  <li className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${milestone.complete ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-[var(--border)] bg-white text-[var(--text-secondary)]"}`} key={milestone.key}>
                    {milestone.complete ? <Check className="size-3.5 shrink-0" /> : <CircleDot className="size-3.5 shrink-0" />}
                    <span>{milestone.label}</span>
                  </li>
                ))}
              </ol>
            </div>
            <ol className="divide-y divide-[var(--border)]">
              {journey.events.map((event) => (
                <li className="grid gap-3 p-5 sm:grid-cols-[7rem_1fr_12rem]" key={event.id}>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[.12em] text-[var(--teal)]">
                      {event.stage}
                    </p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">
                      {displayDate(event.occurredAt, true)}
                    </p>
                  </div>
                  <div>
                    <strong className="text-sm">{event.title}</strong>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">
                      {event.summary || "Evidence recorded"}
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-sm font-semibold">{event.actor}</p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">
                      {event.actorRole ? titleCase(event.actorRole) : "Recorded actor"}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </article>
          );
        })}
        {!visible.length && (
          <div className="surface p-10 text-center">
            <h2 className="font-serif text-xl">No matching purchase journey</h2>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              Requisitions and their accountable events will appear here.
            </p>
          </div>
        )}
      </section>

      <details className="surface overflow-hidden">
        <summary className="flex cursor-pointer items-center gap-3 p-5 font-semibold">
          <FileJson2 className="size-5 text-[var(--teal)]" />
          Technical evidence ledger ({data.audit.length})
        </summary>
        <p className="border-t border-[var(--border)] px-5 py-3 text-sm text-[var(--text-secondary)]">
          Immutable database state changes for forensic review. Business users
          should normally use the purchase journeys above.
        </p>
        <div className="overflow-x-auto border-t border-[var(--border)]">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Actor</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {data.audit
                .filter((row: any) =>
                  matches(
                    query,
                    row.action,
                    row.entity_type,
                    row.actor_name,
                    row.actor_email,
                    profileName(row.profiles),
                    row.correlation_id,
                    JSON.stringify(row.metadata),
                  ),
                )
                .map((row: any) => (
                  <tr key={row.id}>
                    <td>{displayDate(row.created_at, true)}</td>
                    <td><strong>{titleCase(row.action)}</strong></td>
                    <td>{titleCase(row.entity_type)}</td>
                    <td>
                      <strong>{row.actor_name || row.metadata?.actor_snapshot?.name || profileName(row.profiles)}</strong>
                      {(row.actor_role || row.metadata?.actor_snapshot?.role) && (
                        <span className="mt-1 block text-xs text-[var(--text-muted)]">
                          {titleCase(row.actor_role || row.metadata?.actor_snapshot?.role)}
                        </span>
                      )}
                    </td>
                    <td>
                      <details>
                        <summary className="cursor-pointer text-sm text-[var(--action)]">
                          View immutable record
                        </summary>
                        <pre className="mt-2 max-w-xl overflow-auto whitespace-pre-wrap rounded-xl bg-[var(--surface-secondary)] p-3 text-xs">
                          {JSON.stringify(
                            {
                              entity_id: row.entity_id,
                              before: row.before_state,
                              after: row.after_state,
                              metadata: row.metadata,
                              correlation_id: row.correlation_id,
                            },
                            null,
                            2,
                          )}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function AuditMetric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof ShieldCheck;
  label: string;
  value: number;
}) {
  return (
    <div className="surface flex items-center gap-4 p-4">
      <span className="grid size-10 place-items-center rounded-xl bg-[var(--teal-soft)] text-[var(--teal)]">
        <Icon className="size-5" />
      </span>
      <div>
        <strong className="block text-xl tabular-nums">{value}</strong>
        <span className="text-xs text-[var(--text-secondary)]">{label}</span>
      </div>
    </div>
  );
}
